/*
 * Copyright (c) 2022 CalAmp Corp.  All Rights Reserved
 */
import {useCallback, useRef, useState} from 'react';
import uniq from 'lodash.uniq';
import {error} from '@ion/core/src/logger/logger';
import GlobalGroupsSearch from '@ion/features/src/global-groups/search';
import {getOptionId} from '../../filters/TreeFilterUtils';
import {CachedItem, TreeUpdateType} from '../../types/types';
import {
  getAllChildKeys,
  getChildrenKeys,
  getCurrentKeys,
  getIndividualAssetKeys,
  getParentKey
} from '../../utils/Keys';
import AssetTreeLoader from '../utils/AssetTreeLoader';
import DeviceLoader from '../utils/DeviceLoader';

export const rootKey = '0';

export interface TreeKeys {
  expandedKeys: string[];
  checkedKeys: string[];
}

interface AddingItemToTree {
  onUpdateItemStored: Function,
  addItemToTree: Function,
  updateKeys: Function,
  checkRequired: boolean,
  isFromSavedFilter: boolean
  updateTreeNodes: any,
  cachedData: any,
  target: any,
  treeNodes: any
  isFromTreeSearch?: boolean
}

interface UpdateKeys {
  id: string,
  itemKey: string
}

interface UpdateTreeKeys {
  keys: string[],
  expandedKeys: UpdateKeys[],
  isLastItem: boolean,
  checkRequired: boolean,
  updateKeys: Function,
  isFromTreeSearch: boolean
}

enum RequestDataType {
  Assets = 'assets',
  Devices = 'devices'
}

/**
 * Tree view hook to handle expand, select and checked.
 * Also it contains utility for tree nodes and keys.
 * keysRef contains a key/value pair of node path and group id.
 * Example: {'0': 123, '0-1': 12345}
 * treeNodesRef contains reference to the tree nodes constructed.
 *
 * ```typescript
 *   import {useTreeSelection} from '@ion/dls/src/selectiontree/hooks';
 *
 *   const treeHandlers = useTreeSelection(updateKeysActionFn);
 * ```
 */
export const useTreeSelection = () => {
  const treeNodesRef                                  = useRef(null);
  const keysRef                                       = useRef(null);
  const [checkedKeys, setCheckedKeys]                 = useState([]);
  const [defaultExpandedKeys, setDefaultExpandedKeys] = useState<string[]>([]);
  const [defaultSelectedKeys, setDefaultSelectedKeys] = useState([]);

  const updateTreeNodesValue = (treeNodes) => {
    treeNodesRef.current = treeNodes;
  };

  const setAccountsToSearch = (accounts) => {
    GlobalGroupsSearch.setAccounts(accounts);
  };

  const updatedCheckedKeys = useCallback(function(keys: string[]) {
    setCheckedKeys(keys);
  }, []);

  const updatedExpandedKeys = useCallback(function(keys: string[]) {
    setDefaultExpandedKeys(keys);
  }, []);

  const findGroupsSearchResults = async({
    searchValue: groupNameId,
    maxResultLength,
    deviceFilter = '',
    isAccountOnly = false,
    includeAssets = false,
    includeSCDevice = false
  }) => {
    GlobalGroupsSearch.findResults(groupNameId, isAccountOnly);
    const results = GlobalGroupsSearch.fetchResults(1, maxResultLength);
    const groupFound = results?.map(({item: {title, accountId}}) => ({title, id: accountId, isAsset: false}));
    try {
      const currentGlobalGroup = {};
      Object.values(keysRef?.current).forEach(groupId => {
        currentGlobalGroup[`${groupId}`] = true;
      });
      const promises = [];
      if (includeAssets) {
        promises.push(AssetTreeLoader.requestSearch(groupNameId, currentGlobalGroup, maxResultLength));
      }
      if (includeSCDevice) {
        promises.push(DeviceLoader.requestSearch({groupNameId, currentGlobalGroup, deviceFilter, maxResultLength}));
      }
      const response = (await Promise.allSettled(promises))
        .forEach((res) => res.status === 'fulfilled' && (groupFound.push(...res?.value)));
    } catch (e) {
      error(e);
    };
    return groupFound;
  };

  const searchAndFilterGlobalGroups = async(
    title: string,
    filterFn: any,
    maxResultLength: number = 10
  ) => {
    const globalGroupResult = await findGroupsSearchResults({
      searchValue   : title,
      maxResultLength,
      isAccountOnly : true
    });
    const filteredResults = globalGroupResult.filter(filterFn);
    return filteredResults?.[0]?.item ?? filteredResults?.[0];
  };

  const addKey = useCallback((expandedKeys: string[], key: string) => {
    !expandedKeys.includes(key) && setDefaultExpandedKeys([...expandedKeys, key]);
  }, []);

  const removeKey = useCallback((expandedKeys: string[], key: string, children: any) => {
    if (key !== rootKey && expandedKeys?.length > 1) {
      const recursiveKeys       = getAllChildKeys([key], children);
      const updatedExpandedKeys = getCurrentKeys(
        expandedKeys,
        (nextKey: string) => !recursiveKeys.includes(nextKey)
      );
      setDefaultExpandedKeys([...updatedExpandedKeys]);
    } else {
      let keys = getCurrentKeys(
        expandedKeys,
        (nextKey: string) => nextKey !== key
      );
      if (!keys?.length && key !== rootKey) {
        keys = [rootKey];
      }
      setDefaultExpandedKeys(keys);
    }
  }, []);

  const clearSelectedKeys = () => {
    defaultSelectedKeys.length && setDefaultSelectedKeys([]);
  };

  const onExpanded = useCallback((info) => {
    const {expanded, node: {key, children}} = info;
    if (expanded) {
      addKey(defaultExpandedKeys, key);
    } else {
      removeKey(defaultExpandedKeys, key, children);
    }
    clearSelectedKeys();
  }, [defaultExpandedKeys, clearSelectedKeys]);

  const onChecked = useCallback((checkedKeys) => {
    setCheckedKeys(checkedKeys);
  }, [defaultSelectedKeys]);

  const onClose = useCallback(function(stateUpdateFunction, resetCheckedKey = true) {
    stateUpdateFunction(keysRef.current, TreeUpdateType.SET_GROUPS_NODE_KEY_MAP);
    stateUpdateFunction(defaultExpandedKeys, TreeUpdateType.SET_GROUP_FILTERS_EXPANDED);
    resetCheckedKey && setCheckedKeys([]);
  }, [keysRef, checkedKeys, defaultExpandedKeys]);

  /**
   * Defines a utility to save the expanded tree nodes
   */
  const saveExpandedKeys = useCallback((updateKeys: Function) => {
    updateKeys?.(defaultExpandedKeys, TreeUpdateType.SET_GROUP_FILTERS_EXPANDED);
    clearSelectedKeys();
  }, [defaultExpandedKeys, clearSelectedKeys]);

  const updateKeysMap = useCallback(function(keys, stateUpdateFunction) {
    keysRef.current = keys;
    stateUpdateFunction?.(keysRef.current, TreeUpdateType.SET_GROUPS_NODE_KEY_MAP);
  }, [keysRef]);

  const setKeyUpdates = useCallback(function(keyToUpdates: UpdateKeys[]) {
    if (keyToUpdates?.length) {
      const expandedKeys = [];
      const itemsLength  = keyToUpdates.length - 1;
      keyToUpdates.forEach(({id, itemKey}, index) => {
        if (keysRef.current && id?.length) {
          const isLastItem = index === itemsLength;
          let keyToExpand;
          if (!itemKey && keysRef.current) {
            keyToExpand = Object.entries(keysRef.current)
              .filter(([, value]) => value.toString() === id)[0] || [];
          } else {
            keyToExpand = [itemKey];
          }
          setDefaultSelectedKeys([keyToExpand?.[0]]);
          if (keyToExpand?.length) {
            const splitKeys = keyToExpand[0]?.split('-');
            if (splitKeys?.length) {
              splitKeys.splice(-1);
              const tempExpandedKeys = splitKeys.reverse().reduce((acc, curr) => (
                [...acc.map(c => `${curr}-${c}`), curr]
              ), []) || [];
              // check if selected node is not phantom or asset node
              if (keyToExpand?.[0]?.[0] !== '-') {
                tempExpandedKeys?.push(keyToExpand?.[0]);
              }
              expandedKeys.push(...tempExpandedKeys);
              isLastItem && setDefaultExpandedKeys([...defaultExpandedKeys, ...expandedKeys]);
            }
          }
        }
      });
    } else {
      setDefaultSelectedKeys([]);
      setDefaultExpandedKeys([]);
    }
  }, [checkedKeys, defaultExpandedKeys, keysRef.current]);

  const getGroupsForCheckedKeys = useCallback(async(checkedPaths) => {
    const groups = await Promise.all(checkedPaths.map(async(path: string) => {
      const keypath = path?.split('')[0] === '-' ? path?.slice(1, path?.length) : path;
      return await searchAndFilterGlobalGroups(
        keysRef.current[keypath],
        (item) => item?.id === `${keysRef.current[keypath]}`);
    }));
    return groups;
  }, [keysRef]);

  const getGroupTitleForPath = useCallback(async function(path, assets) {
    if (path) {
      const assetPath = Object.values(assets).find(({key}) => key === path);
      if (assetPath) {
        return (assetPath as CachedItem).title;
      };
      const groups = await findGroupsSearchResults({
        searchValue     : keysRef.current[path],
        maxResultLength : 1,
        isAccountOnly   : true
      });
      return groups.find(({id}) => parseInt(id) === keysRef.current[path])?.title;
    }
    return null;
  }, [keysRef]);

  /**
   * @onAddingItemToTree add asset or group to the treeNode
   * and update checkedKeys and expandedKeys
   */
  const onAddingItemToTree = function({
    onUpdateItemStored,
    updateTreeNodes,
    addItemToTree,
    updateKeys,
    cachedData,
    target,
    treeNodes,
    checkRequired = false,
    isFromSavedFilter = false,
    isFromTreeSearch = false
  }: AddingItemToTree) {
    const items        = !Array.isArray(target) ? [target] : target;
    const itemsLength  = items.length - 1;
    let keys         = uniq([...checkedKeys]); // To prevent duplicate keys
    const expandedKeys = [];
    items?.forEach((item, index) => {
      const isLastItem = index === itemsLength;
      if (item?.isAsset || item?.isDevice) {
        // Check if the asset is already cached in the store
        const id = getOptionId(item);
        const {key: itemKeyCached} = cachedData[id] || {};
        // if not, create an asset info and update the store with treeNodes
        if (!itemKeyCached) {
          const currentTreeNodes = treeNodes || treeNodesRef.current;
          const {treeNode: updatedTree, itemKey} = addItemToTree(currentTreeNodes, item);
          const itemInfo = {
            accountId : item?.id,
            title     : item?.title,
            isAsset   : item.isAsset,
            isDevice  : item.isDevice,
            key       : itemKey,
            id
          };
          onUpdateItemStored(itemInfo);
          updateTreeNodes([updatedTree]);
          updateTreeNodesValue(updatedTree);
          // Add the asset to the checkedKeys and expandedKeys
          expandedKeys.push({id: id.toString(), itemKey});
          keys.push(itemKey);
          updateTreeKeys({
            keys,
            expandedKeys,
            isLastItem,
            checkRequired,
            isFromTreeSearch,
            updateKeys
          });
        } else {
          // Else, use itemKeyCached to expand and highlight the item selected
          expandedKeys.push({id: id.toString(), itemKey: itemKeyCached});
          keys.push(itemKeyCached);
          updateTreeKeys({
            keys,
            expandedKeys,
            isLastItem,
            checkRequired,
            isFromTreeSearch,
            updateKeys
          });
        }
      }
      if (!checkRequired) {
        updateKeys(checkedKeys, TreeUpdateType.SET_GROUP_FILTERS_CHECKED);
      }
      if (!item?.isAsset && !item?.isDevice) {
        expandedKeys.push({id: item?.id, itemKey: null});
        if (isFromTreeSearch) {
          const parentKey       = getParentKey(keysRef.current, item);
          const childrenKeys    = getChildrenKeys(keysRef.current, parentKey);
          const singleAssetKeys = getIndividualAssetKeys(cachedData, parentKey);
          keys = [parentKey, ...childrenKeys, ...singleAssetKeys, ...keys];
        } else {
          keys.push(item?.key);
        }
        if (isLastItem) {
          setKeyUpdates(expandedKeys);
          if (isFromSavedFilter) {
            updatedCheckedKeys(keys);
          }
        }
      }
    });
    updateTreeKeys({
      keys,
      expandedKeys,
      isLastItem : true,
      checkRequired,
      isFromTreeSearch,
      updateKeys
    });
  };

  const updateTreeKeys = useCallback(({
    keys,
    isLastItem,
    expandedKeys,
    checkRequired,
    isFromTreeSearch,
    updateKeys
  }: UpdateTreeKeys) => {
    if (isLastItem) {
      setKeyUpdates(expandedKeys);
      if (checkRequired) {
        if (!isFromTreeSearch) {
          updateKeys(keys, TreeUpdateType.SET_GROUP_FILTERS_CHECKED);
        }
        updatedCheckedKeys(keys);
      }
    }
  }, [setKeyUpdates, updatedCheckedKeys]);

  return {
    checkedKeys,
    defaultExpandedKeys,
    defaultSelectedKeys,
    onAddingItemToTree,
    findGroupsSearchResults,
    getGroupsForCheckedKeys,
    setKeyUpdates,
    getGroupTitleForPath,
    setAccountsToSearch,
    saveExpandedKeys,
    updateTreeNodesValue,
    updatedExpandedKeys,
    updatedCheckedKeys,
    updateKeysMap,
    onExpanded,
    onChecked,
    onClose
  };
};
